import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useZones } from '../hooks/useZones';
import { useBoxes, useCreateBox, useUpdateBox, useDeleteBox } from '../hooks/useBoxes';
import { useSites, useCreateSite, useUpdateSite, useDeleteSite } from '../hooks/useSites';
import { useBatiments, useCreateBatiment, useUpdateBatiment, useDeleteBatiment } from '../hooks/useBatiments';
import { usePieces, useCreatePiece, useUpdatePiece, useDeletePiece } from '../hooks/usePieces';
import ThemeFilter from '../components/ThemeFilter';
import ZoneCard from '../components/ZoneCard';
import BoxCard from '../components/BoxCard';
import SiteCard from '../components/SiteCard';
import BatimentCard from '../components/BatimentCard';
import PieceCard from '../components/PieceCard';
import CreateSiteModal, { SiteFormData } from '../components/CreateSiteModal';
import CreateBatimentModal, { BatimentFormData } from '../components/CreateBatimentModal';
import CreatePieceModal, { PieceFormData } from '../components/CreatePieceModal';
import CreateZoneModal, { ZoneFormData } from '../components/CreateZoneModal';
import CreateBoxModal, { BoxFormData } from '../components/CreateBoxModal';
import MobileSearch from '../components/MobileSearch';
import LieuxFilters from '../components/LieuxFilters';
import SortGroupControls from '../components/SortGroupControls';
import SelectionControls from '../components/SelectionControls';
import BulkActionsLieux from '../components/BulkActionsLieux';
import BulkUpdateZonesModal from '../components/BulkUpdateZonesModal';
import BulkDeleteZonesModal from '../components/BulkDeleteZonesModal';
import BulkUpdateBoxesModal from '../components/BulkUpdateBoxesModal';
import BulkMoveBoxesModal from '../components/BulkMoveBoxesModal';
import BulkDeleteBoxesModal from '../components/BulkDeleteBoxesModal';
import BulkAddToBoxModal from '../components/BulkAddToBoxModal';
import BulkPhotoLieuModal, { BulkPhotoLieuTarget } from '../components/BulkPhotoLieuModal';
import BulkUpdateSitesModal from '../components/BulkUpdateSitesModal';
import BulkDeleteSitesModal from '../components/BulkDeleteSitesModal';
import BulkUpdateBatimentsModal from '../components/BulkUpdateBatimentsModal';
import BulkDeleteBatimentsModal from '../components/BulkDeleteBatimentsModal';
import BulkUpdatePiecesModal from '../components/BulkUpdatePiecesModal';
import BulkDeletePiecesModal from '../components/BulkDeletePiecesModal';
import { sortItems } from '../utils/sortingUtils';
import { groupItems } from '../utils/groupingUtils';
import { useToastContext } from '../contexts/ToastContext';
import { isBulkPhotoLieuEnabled } from '../utils/featureEnv';

const LieuxPage: React.FC = () => {
  const { showSuccess, showError, showWarning, showInfo } = useToastContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = usePermissions();
  const [activeTab, setActiveTab] = useState<'sites' | 'batiments' | 'pieces' | 'zones' | 'boxes'>('sites');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  
  // Filtres, tri et groupement
  const [filters, setFilters] = useState({
    search: '',
    siteId: '',
    batimentId: '',
    pieceId: '',
    theme: '',
  });
  const [sortBy, setSortBy] = useState('nom-asc');
  const [groupBy, setGroupBy] = useState('none');
  const [showFilters, setShowFilters] = useState(false);
  
  // Sélection bulk
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);
  const [selectedBatimentIds, setSelectedBatimentIds] = useState<string[]>([]);
  const [selectedPieceIds, setSelectedPieceIds] = useState<string[]>([]);
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
  const [selectedBoxIds, setSelectedBoxIds] = useState<string[]>([]);
  
  // Modals bulk sites
  const [isBulkUpdateSitesOpen, setIsBulkUpdateSitesOpen] = useState(false);
  const [isBulkDeleteSitesOpen, setIsBulkDeleteSitesOpen] = useState(false);
  
  // Modals bulk batiments
  const [isBulkUpdateBatimentsOpen, setIsBulkUpdateBatimentsOpen] = useState(false);
  const [isBulkDeleteBatimentsOpen, setIsBulkDeleteBatimentsOpen] = useState(false);
  
  // Modals bulk pieces
  const [isBulkUpdatePiecesOpen, setIsBulkUpdatePiecesOpen] = useState(false);
  const [isBulkDeletePiecesOpen, setIsBulkDeletePiecesOpen] = useState(false);
  
  // Modals bulk zones
  const [isBulkUpdateZonesOpen, setIsBulkUpdateZonesOpen] = useState(false);
  const [isBulkDeleteZonesOpen, setIsBulkDeleteZonesOpen] = useState(false);
  
  // Modals bulk boxes
  const [isBulkUpdateBoxesOpen, setIsBulkUpdateBoxesOpen] = useState(false);
  const [isBulkMoveBoxesOpen, setIsBulkMoveBoxesOpen] = useState(false);
  const [isBulkDeleteBoxesOpen, setIsBulkDeleteBoxesOpen] = useState(false);
  const [isBulkAddToBoxOpen, setIsBulkAddToBoxOpen] = useState(false);
  const [boxForBulkAdd, setBoxForBulkAdd] = useState<any>(null);
  const [isBulkPhotoLieuOpen, setIsBulkPhotoLieuOpen] = useState(false);
  const [bulkPhotoTarget, setBulkPhotoTarget] = useState<BulkPhotoLieuTarget | null>(null);
  const bulkPhotoLieuEnabled = isBulkPhotoLieuEnabled();

  const openBulkPhotoModal = (target: BulkPhotoLieuTarget) => {
    setBulkPhotoTarget(target);
    setIsBulkPhotoLieuOpen(true);
  };

  // Modals state
  const [isCreateSiteModalOpen, setIsCreateSiteModalOpen] = useState(false);
  const [isCreateBatimentModalOpen, setIsCreateBatimentModalOpen] = useState(false);
  const [isCreatePieceModalOpen, setIsCreatePieceModalOpen] = useState(false);
  const [isCreateZoneModalOpen, setIsCreateZoneModalOpen] = useState(false);
  const [isCreateBoxModalOpen, setIsCreateBoxModalOpen] = useState(false);
  
  // Editing state
  const [editingSite, setEditingSite] = useState<any>(null);
  const [editingBatiment, setEditingBatiment] = useState<any>(null);
  const [editingPiece, setEditingPiece] = useState<any>(null);
  const [editingZone, setEditingZone] = useState<any>(null);
  const [editingBox, setEditingBox] = useState<any>(null);

  // Utiliser les vraies données de l'API
  const { data: sitesData, isLoading: sitesLoading, error: sitesError } = useSites();
  const { data: batimentsData, isLoading: batimentsLoading, error: batimentsError } = useBatiments();
  const { data: piecesData, isLoading: piecesLoading, error: piecesError } = usePieces();
  const { data: zonesData, isLoading: zonesLoading, error: zonesError } = useZones();
  const { data: boxesData, isLoading: boxesLoading, error: boxesError } = useBoxes();
  const createBoxMutation = useCreateBox();
  const updateBoxMutation = useUpdateBox();
  const deleteBoxMutation = useDeleteBox();
  
  // Mutations
  const createSiteMutation = useCreateSite();
  const updateSiteMutation = useUpdateSite();
  const deleteSiteMutation = useDeleteSite();
  const createBatimentMutation = useCreateBatiment();
  const updateBatimentMutation = useUpdateBatiment();
  const deleteBatimentMutation = useDeleteBatiment();

  const sites = sitesData || [];
  const batiments = batimentsData || [];
  const pieces = piecesData || [];
  const zones = zonesData || [];
  const boxes = boxesData || [];

  // Appliquer les filtres depuis l'URL au chargement
  useEffect(() => {
    const filterParam = searchParams.get('filter');
    if (filterParam && zones.length > 0) {
      const [key, value] = filterParam.split(':');
      switch (key) {
        case 'avecTheme':
          if (value === 'true') {
            // Filtrer pour montrer seulement les zones avec thème
            const zonesWithTheme = zones.filter(zone => zone.theme && zone.theme.trim() !== '');
            const themes = [...new Set(zonesWithTheme.map(zone => zone.theme))];
            setSelectedThemes(themes);
          }
          break;
        case 'type':
          if (value === 'boxes') {
            setActiveTab('boxes');
          }
          break;
        case 'horsDomicile':
          if (value === 'true') {
            setActiveTab('boxes');
            // TODO: Filtrer les boxes hors domicile
          }
          break;
        default:
          break;
      }
    }
  }, [searchParams]); // Retirer 'zones' des dépendances pour éviter la boucle infinie

  // Extraire tous les thèmes uniques des zones et boxes
  const allThemes = useMemo(() => {
    const zoneThemes = zones
      .map(zone => zone.theme)
      .filter(theme => theme && theme.trim() !== '');
    
    const boxThemes = boxes
      .map(box => box.theme)
      .filter(theme => theme && theme.trim() !== '');
    
    const themes = [...zoneThemes, ...boxThemes]
      .filter((theme, index, self) => self.indexOf(theme) === index)
      .sort();
    return themes;
  }, [zones, boxes]);

  // Les filteredZones et filteredBoxes sont définis plus bas avec la logique complète de filtrage

  const handleEdit = (item: any, type: string) => {
    switch (type) {
      case 'site':
        setEditingSite(item);
        setIsCreateSiteModalOpen(true);
        break;
      case 'batiment':
        setEditingBatiment(item);
        setIsCreateBatimentModalOpen(true);
        break;
      case 'piece':
        setEditingPiece(item);
        setIsCreatePieceModalOpen(true);
        break;
      case 'zone':
        setEditingZone(item);
        setIsCreateZoneModalOpen(true);
        break;
      case 'box':
        setEditingBox(item);
        setIsCreateBoxModalOpen(true);
        break;
      default:
        setEditingItem({ ...item, type });
        setShowCreateForm(true);
    }
  };

  const handleDelete = async (id: string, type: string) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ce(tte) ${type} ?`)) {
      try {
        if (type === 'site') {
          await deleteSiteMutation.mutateAsync(id);
          showSuccess('Site supprimé', 'Le site a été supprimé avec succès.');
        } else if (type === 'batiment') {
          await deleteBatimentMutation.mutateAsync(id);
          showSuccess('Bâtiment supprimé', 'Le bâtiment a été supprimé avec succès.');
        } else if (type === 'box') {
          await deleteBoxMutation.mutateAsync(id);
          showSuccess('Box supprimée', 'La box a été supprimée avec succès.');
        } else {
          console.log(`Supprimer ${type}:`, id);
        }
      } catch (error: any) {
        console.error(`Erreur lors de la suppression du ${type}:`, error);
        showError('Erreur de suppression', error.response?.data?.message || error.message);
      }
    }
  };

  // Handler pour créer/modifier un site
  const handleCreateSite = async (data: SiteFormData) => {
    try {
      if (editingSite) {
        await updateSiteMutation.mutateAsync({ id: editingSite.id, data });
        showSuccess('Site modifié', 'Les modifications ont été enregistrées.');
      } else {
        await createSiteMutation.mutateAsync(data);
        showSuccess('Site créé', 'Le site a été créé avec succès.');
      }
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  // Handler pour créer/modifier un bâtiment
  const handleCreateBatiment = async (data: BatimentFormData) => {
    try {
      if (editingBatiment) {
        await updateBatimentMutation.mutateAsync({ id: editingBatiment.id, data });
        showSuccess('Bâtiment modifié', 'Les modifications ont été enregistrées.');
      } else {
        await createBatimentMutation.mutateAsync(data);
        showSuccess('Bâtiment créé', 'Le bâtiment a été créé avec succès.');
      }
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  // Handler pour créer/modifier une pièce
  const handleCreatePiece = async (data: PieceFormData) => {
    try {
      console.log('Création/modification de la pièce:', data);
      // TODO: Implémenter l'API
      showSuccess(editingPiece ? 'Pièce modifiée' : 'Pièce créée', editingPiece ? 'Les modifications ont été enregistrées.' : 'La pièce a été créée avec succès.');
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.message);
    }
  };

  // Handler pour créer/modifier une zone
  const handleCreateZone = async (data: ZoneFormData) => {
    try {
      console.log('Création/modification de la zone:', data);
      // TODO: Implémenter l'API
      showSuccess(editingZone ? 'Zone modifiée' : 'Zone créée', editingZone ? 'Les modifications ont été enregistrées.' : 'La zone a été créée avec succès.');
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.message);
    }
  };

  // Handler pour créer/modifier une box
  const handleCreateBox = async (data: BoxFormData) => {
    try {
      const payload = {
        nom: data.nom,
        code: data.code,
        theme: data.theme || undefined,
        numero: data.numero || undefined,
        zoneId: data.zoneId,
        pieceId: data.pieceId,
      };

      if (editingBox?.id) {
        await updateBoxMutation.mutateAsync({ id: String(editingBox.id), data: payload });
        showSuccess('Box modifiée', 'Les modifications ont été enregistrées.');
      } else {
        await createBoxMutation.mutateAsync(payload);
        showSuccess('Box créée', 'La box a été créée avec succès.');
      }
      setEditingBox(null);
    } catch (error: any) {
      console.error('Erreur:', error);
      showError('Erreur', error.response?.data?.message || error.message);
    }
  };

  // Handlers pour filtres
  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      siteId: '',
      batimentId: '',
      pieceId: '',
      theme: '',
    });
  };

  // Handlers pour sélection bulk zones
  const handleToggleZoneSelection = (zoneId: string) => {
    setSelectedZoneIds(prev =>
      prev.includes(zoneId) ? prev.filter(id => id !== zoneId) : [...prev, zoneId]
    );
  };

  const handleSelectAllZones = () => {
    const allZoneIds = filteredZones.map((zone: any) => zone.id);
    setSelectedZoneIds(allZoneIds);
  };

  const handleClearZoneSelection = () => {
    setSelectedZoneIds([]);
  };

  // Handlers pour sélection bulk boxes
  const handleToggleBoxSelection = (boxId: string) => {
    setSelectedBoxIds(prev =>
      prev.includes(boxId) ? prev.filter(id => id !== boxId) : [...prev, boxId]
    );
  };

  const handleSelectAllBoxes = () => {
    const allBoxIds = filteredBoxes.map((box: any) => box.id);
    setSelectedBoxIds(allBoxIds);
  };

  const handleClearBoxSelection = () => {
    setSelectedBoxIds([]);
  };

  // Handlers pour sélection bulk sites
  const handleToggleSiteSelection = (siteId: string) => {
    setSelectedSiteIds(prev =>
      prev.includes(siteId) ? prev.filter(id => id !== siteId) : [...prev, siteId]
    );
  };

  const handleSelectAllSites = () => {
    const allSiteIds = filteredSites.map((site: any) => site.id);
    setSelectedSiteIds(allSiteIds);
  };

  const handleClearSiteSelection = () => {
    setSelectedSiteIds([]);
  };

  // Handlers pour sélection bulk batiments
  const handleToggleBatimentSelection = (batimentId: string) => {
    setSelectedBatimentIds(prev =>
      prev.includes(batimentId) ? prev.filter(id => id !== batimentId) : [...prev, batimentId]
    );
  };

  const handleSelectAllBatiments = () => {
    const allBatimentIds = filteredBatiments.map((batiment: any) => batiment.id);
    setSelectedBatimentIds(allBatimentIds);
  };

  const handleClearBatimentSelection = () => {
    setSelectedBatimentIds([]);
  };

  // Handlers pour sélection bulk pieces
  const handleTogglePieceSelection = (pieceId: string) => {
    setSelectedPieceIds(prev =>
      prev.includes(pieceId) ? prev.filter(id => id !== pieceId) : [...prev, pieceId]
    );
  };

  const handleSelectAllPieces = () => {
    const allPieceIds = filteredPieces.map((piece: any) => piece.id);
    setSelectedPieceIds(allPieceIds);
  };

  const handleClearPieceSelection = () => {
    setSelectedPieceIds([]);
  };

  // Handlers pour actions bulk sites
  const handleBulkUpdateSites = () => setIsBulkUpdateSitesOpen(true);
  const handleBulkDeleteSites = () => setIsBulkDeleteSitesOpen(true);

  // Handlers pour actions bulk batiments
  const handleBulkUpdateBatiments = () => setIsBulkUpdateBatimentsOpen(true);
  const handleBulkDeleteBatiments = () => setIsBulkDeleteBatimentsOpen(true);

  // Handlers pour actions bulk pieces
  const handleBulkUpdatePieces = () => setIsBulkUpdatePiecesOpen(true);
  const handleBulkDeletePieces = () => setIsBulkDeletePiecesOpen(true);

  // Handlers pour actions bulk zones
  const handleBulkUpdateZones = () => setIsBulkUpdateZonesOpen(true);
  const handleBulkDeleteZones = () => setIsBulkDeleteZonesOpen(true);

  // Handlers pour actions bulk boxes
  const handleBulkUpdateBoxes = () => setIsBulkUpdateBoxesOpen(true);
  const handleBulkMoveBoxes = () => setIsBulkMoveBoxesOpen(true);
  const handleBulkDeleteBoxes = () => setIsBulkDeleteBoxesOpen(true);

  // Fermeture modals bulk avec nettoyage
  const handleCloseBulkZonesModal = () => {
    setIsBulkUpdateZonesOpen(false);
    setIsBulkDeleteZonesOpen(false);
    setSelectedZoneIds([]);
  };

  const handleCloseBulkBoxesModal = () => {
    setIsBulkUpdateBoxesOpen(false);
    setIsBulkMoveBoxesOpen(false);
    setIsBulkDeleteBoxesOpen(false);
    setSelectedBoxIds([]);
  };

  // Filtrage, tri et groupement des sites
  const filteredSites = useMemo(() => {
    let result = sites;

    // Recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((site: any) =>
        site.nom?.toLowerCase().includes(searchLower) ||
        site.trigram?.toLowerCase().includes(searchLower) ||
        site.adresse?.toLowerCase().includes(searchLower)
      );
    }

    return result;
  }, [sites, filters.search]);

  // Filtrage, tri et groupement des batiments
  const filteredBatiments = useMemo(() => {
    let result = batiments;

    // Recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((batiment: any) =>
        batiment.nom?.toLowerCase().includes(searchLower) ||
        batiment.trigram?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre site
    if (filters.siteId) {
      result = result.filter((batiment: any) => batiment.siteId === filters.siteId);
    }

    return result;
  }, [batiments, filters.search, filters.siteId]);

  // Filtrage, tri et groupement des pieces
  const filteredPieces = useMemo(() => {
    let result = pieces;

    // Recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((piece: any) =>
        piece.nom?.toLowerCase().includes(searchLower) ||
        piece.numero?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre site (via batiment -> site)
    if (filters.siteId) {
      result = result.filter((piece: any) => piece.batiment?.siteId === filters.siteId);
    }

    // Filtre bâtiment
    if (filters.batimentId) {
      result = result.filter((piece: any) => piece.batimentId === filters.batimentId);
    }

    return result;
  }, [pieces, filters.search, filters.siteId, filters.batimentId]);

  // Filtrage, tri et groupement des zones
  const filteredZones = useMemo(() => {
    let result = zones;

    // Recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((zone: any) =>
        zone.nom?.toLowerCase().includes(searchLower) ||
        zone.code?.toLowerCase().includes(searchLower) ||
        zone.theme?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre site (via piece -> batiment -> site)
    if (filters.siteId) {
      result = result.filter((zone: any) => zone.piece?.batiment?.siteId === filters.siteId);
    }

    // Filtre bâtiment
    if (filters.batimentId) {
      result = result.filter((zone: any) => zone.piece?.batimentId === filters.batimentId);
    }

    // Filtre pièce
    if (filters.pieceId) {
      result = result.filter((zone: any) => zone.pieceId === filters.pieceId);
    }

    // Filtre thème
    if (filters.theme) {
      result = result.filter((zone: any) =>
        zone.theme?.includes(filters.theme)
      );
    }

    return result;
  }, [zones, filters]);

  // Filtrage, tri et groupement des boxes
  const filteredBoxes = useMemo(() => {
    let result = boxes;

    // Recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter((box: any) =>
        box.nom?.toLowerCase().includes(searchLower) ||
        box.code?.toLowerCase().includes(searchLower) ||
        box.theme?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre thème
    if (filters.theme) {
      result = result.filter((box: any) =>
        box.theme?.includes(filters.theme)
      );
    }

    return result;
  }, [boxes, filters]);

  // Tri et groupement
  const sortedSites = useMemo(() => sortItems(filteredSites, sortBy), [filteredSites, sortBy]);
  const groupedSites = useMemo(() => groupItems(sortedSites, groupBy), [sortedSites, groupBy]);

  const sortedBatiments = useMemo(() => sortItems(filteredBatiments, sortBy), [filteredBatiments, sortBy]);
  const groupedBatiments = useMemo(() => groupItems(sortedBatiments, groupBy), [sortedBatiments, groupBy]);

  const sortedPieces = useMemo(() => sortItems(filteredPieces, sortBy), [filteredPieces, sortBy]);
  const groupedPieces = useMemo(() => groupItems(sortedPieces, groupBy), [sortedPieces, groupBy]);

  const sortedZones = useMemo(() => sortItems(filteredZones, sortBy), [filteredZones, sortBy]);
  const groupedZones = useMemo(() => groupItems(sortedZones, groupBy), [sortedZones, groupBy]);

  const sortedBoxes = useMemo(() => sortItems(filteredBoxes, sortBy), [filteredBoxes, sortBy]);
  const groupedBoxes = useMemo(() => groupItems(sortedBoxes, groupBy), [sortedBoxes, groupBy]);

  if (sitesLoading || batimentsLoading || piecesLoading || zonesLoading || boxesLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Lieux</h1>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (sitesError || batimentsError || piecesError || zonesError || boxesError) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Lieux</h1>
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-red-800 font-medium">Erreur de chargement</h3>
          <p className="text-red-600 mt-1">
            Impossible de charger les données. Vérifiez votre connexion.
          </p>
        </div>
      </div>
    );
  }

  // Vérifier s'il y a un filtre actif depuis l'URL
  const hasActiveFilter = searchParams.get('filter') !== null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Gestion des Lieux</h1>
        {hasActiveFilter && (
          <p className="text-sm text-blue-600 mt-1">
            🔍 Filtre actif depuis le tableau de bord
          </p>
        )}
      </div>

      {/* Onglets */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('sites')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'sites'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🏢 Sites
          </button>
          <button
            onClick={() => setActiveTab('batiments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'batiments'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🏗️ Bâtiments
          </button>
          <button
            onClick={() => setActiveTab('pieces')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'pieces'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            🚪 Pièces
          </button>
          <button
            onClick={() => setActiveTab('zones')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'zones'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📦 Zones & Armoires
          </button>
          <button
            onClick={() => setActiveTab('boxes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'boxes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            📱 Boxes Mobiles
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'sites' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Sites
              <span className="ml-2 text-sm text-gray-500">
                ({filteredSites.length} site{filteredSites.length > 1 ? 's' : ''})
              </span>
            </h2>
            {permissions.canCreate && (
              <button
                onClick={() => setIsCreateSiteModalOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                + Nouveau Site
              </button>
            )}
          </div>

          {/* Recherche */}
          <MobileSearch
            value={filters.search}
            onChange={(value) => handleFilterChange('search', value)}
            placeholder="Rechercher un site..."
          />

          {/* Bouton toggle filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full md:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            {showFilters ? '🔽 Masquer les filtres' : '🔼 Afficher les filtres'}
          </button>

          {/* Filtres avancés */}
          {showFilters && (
            <LieuxFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />
          )}

          {/* Contrôles de tri et groupement */}
          <SortGroupControls
            sortBy={sortBy}
            onSortChange={setSortBy}
            groupBy={groupBy}
            onGroupChange={setGroupBy}
            type="lieux"
            lieuType="sites"
          />

          {/* Contrôles de sélection */}
          {selectedSiteIds.length > 0 && (permissions.canBulkEdit || permissions.canBulkDelete) && (
            <SelectionControls
              selectedCount={selectedSiteIds.length}
              totalCount={filteredSites.length}
              onSelectAll={handleSelectAllSites}
              onClearSelection={handleClearSiteSelection}
              onBulkUpdate={handleBulkUpdateSites}
              onBulkDelete={handleBulkDeleteSites}
            />
          )}

          {/* Affichage groupé */}
          {groupedSites.map((group: any) => (
            <div key={group.key} className="space-y-3">
              {group.key !== 'none' && (
                <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
                  {group.label}
                  <span className="ml-2 text-sm text-gray-500">
                    ({group.items.length} site{group.items.length > 1 ? 's' : ''})
                  </span>
                </h3>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.items.map((site: any) => (
                  <div key={site.id} className="relative">
                    <SiteCard
                      site={site}
                      onEdit={(site) => handleEdit(site, 'site')}
                      onDelete={(site) => handleDelete(site.id, 'site')}
                      isSelected={selectedSiteIds.includes(site.id)}
                      onToggleSelection={() => handleToggleSiteSelection(site.id)}
                      canEdit={permissions.canEdit}
                      canDelete={permissions.canDelete}
                      canBulk={permissions.canBulkEdit || permissions.canBulkDelete}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {groupedSites.length === 0 && (
            <div className="text-center text-gray-600 py-8">
              Aucun site trouvé.
            </div>
          )}
        </div>
      )}

      {activeTab === 'batiments' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Bâtiments
              <span className="ml-2 text-sm text-gray-500">
                ({filteredBatiments.length} bâtiment{filteredBatiments.length > 1 ? 's' : ''})
              </span>
            </h2>
            {permissions.canCreate && (
              <button
                onClick={() => setIsCreateBatimentModalOpen(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                + Nouveau Bâtiment
              </button>
            )}
          </div>

          {/* Recherche */}
          <MobileSearch
            value={filters.search}
            onChange={(value) => handleFilterChange('search', value)}
            placeholder="Rechercher un bâtiment..."
          />

          {/* Bouton toggle filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full md:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            {showFilters ? '🔽 Masquer les filtres' : '🔼 Afficher les filtres'}
          </button>

          {/* Filtres avancés */}
          {showFilters && (
            <LieuxFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />
          )}

          {/* Contrôles de tri et groupement */}
          <SortGroupControls
            sortBy={sortBy}
            onSortChange={setSortBy}
            groupBy={groupBy}
            onGroupChange={setGroupBy}
            type="lieux"
            lieuType="batiments"
          />

          {/* Contrôles de sélection */}
          {selectedBatimentIds.length > 0 && (
            <SelectionControls
              selectedCount={selectedBatimentIds.length}
              totalCount={filteredBatiments.length}
              onSelectAll={handleSelectAllBatiments}
              onClearSelection={handleClearBatimentSelection}
              onBulkUpdate={handleBulkUpdateBatiments}
              onBulkDelete={handleBulkDeleteBatiments}
            />
          )}

          {/* Affichage groupé */}
          {groupedBatiments.map((group: any) => (
            <div key={group.key} className="space-y-3">
              {group.key !== 'none' && (
                <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
                  {group.label}
                  <span className="ml-2 text-sm text-gray-500">
                    ({group.items.length} bâtiment{group.items.length > 1 ? 's' : ''})
                  </span>
                </h3>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.items.map((batiment: any) => (
                  <div key={batiment.id} className="relative">
                    <BatimentCard
                      batiment={batiment}
                      onEdit={(batiment) => handleEdit(batiment, 'batiment')}
                      onDelete={(batiment) => handleDelete(batiment.id, 'batiment')}
                      isSelected={selectedBatimentIds.includes(batiment.id)}
                      onToggleSelection={() => handleToggleBatimentSelection(batiment.id)}
                      canEdit={permissions.canEdit}
                      canDelete={permissions.canDelete}
                      canBulk={permissions.canBulkEdit || permissions.canBulkDelete}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {groupedBatiments.length === 0 && (
            <div className="text-center text-gray-600 py-8">
              Aucun bâtiment trouvé.
            </div>
          )}
        </div>
      )}

      {activeTab === 'pieces' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Pièces
              <span className="ml-2 text-sm text-gray-500">
                ({filteredPieces.length} pièce{filteredPieces.length > 1 ? 's' : ''})
              </span>
            </h2>
            {permissions.canCreate && (
              <button
                onClick={() => { setEditingPiece(null); setIsCreatePieceModalOpen(true); }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                + Nouvelle Pièce
              </button>
            )}
          </div>

          {/* Recherche */}
          <MobileSearch
            value={filters.search}
            onChange={(value) => handleFilterChange('search', value)}
            placeholder="Rechercher une pièce..."
          />

          {/* Bouton toggle filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full md:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            {showFilters ? '🔽 Masquer les filtres' : '🔼 Afficher les filtres'}
          </button>

          {/* Filtres avancés */}
          {showFilters && (
            <LieuxFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />
          )}

          {/* Contrôles de tri et groupement */}
          <SortGroupControls
            sortBy={sortBy}
            onSortChange={setSortBy}
            groupBy={groupBy}
            onGroupChange={setGroupBy}
            type="lieux"
            lieuType="pieces"
          />

          {/* Contrôles de sélection */}
          {selectedPieceIds.length > 0 && (
            <SelectionControls
              selectedCount={selectedPieceIds.length}
              totalCount={filteredPieces.length}
              onSelectAll={handleSelectAllPieces}
              onClearSelection={handleClearPieceSelection}
              onBulkUpdate={handleBulkUpdatePieces}
              onBulkDelete={handleBulkDeletePieces}
            />
          )}

          {/* Affichage groupé */}
          {groupedPieces.map((group: any) => (
            <div key={group.key} className="space-y-3">
              {group.key !== 'none' && (
                <h3 className="text-lg font-medium text-gray-800 border-b border-gray-200 pb-2">
                  {group.label}
                  <span className="ml-2 text-sm text-gray-500">
                    ({group.items.length} pièce{group.items.length > 1 ? 's' : ''})
                  </span>
                </h3>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.items.map((piece: any) => (
                  <div key={piece.id} className="relative">
                    <PieceCard
                      piece={piece}
                      onEdit={(piece) => handleEdit(piece, 'piece')}
                      onDelete={(piece) => handleDelete(piece.id, 'piece')}
                      isSelected={selectedPieceIds.includes(piece.id)}
                      onToggleSelection={() => handleTogglePieceSelection(piece.id)}
                      canEdit={permissions.canEdit}
                      canDelete={permissions.canDelete}
                      canBulk={permissions.canBulkEdit || permissions.canBulkDelete}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {groupedPieces.length === 0 && (
            <div className="text-center text-gray-600 py-8">
              Aucune pièce trouvée.
            </div>
          )}
        </div>
      )}

      {activeTab === 'zones' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Zones & Armoires
              <span className="ml-2 text-sm text-gray-500">
                ({filteredZones.length} zone{filteredZones.length > 1 ? 's' : ''})
              </span>
            </h2>
            {permissions.canCreate && (
              <button
                onClick={() => { setEditingZone(null); setIsCreateZoneModalOpen(true); }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                + Nouvelle Zone
              </button>
            )}
          </div>

          {/* Recherche */}
          <MobileSearch
            value={filters.search}
            onChange={(value) => handleFilterChange('search', value)}
            placeholder="Rechercher une zone..."
          />

          {/* Bouton toggle filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full md:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            {showFilters ? '🔽 Masquer les filtres' : '🔼 Afficher les filtres'}
          </button>

          {/* Filtres avancés */}
          {showFilters && (
            <LieuxFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />
          )}

          {/* Tri et groupement */}
          <SortGroupControls
            sortBy={sortBy}
            groupBy={groupBy}
            onSortChange={setSortBy}
            onGroupChange={setGroupBy}
            type="lieux"
            lieuType="zones"
          />

          {/* Sélection et actions bulk */}
          <SelectionControls
            selectedIds={selectedZoneIds}
            totalCount={filteredZones.length}
            onSelectAll={handleSelectAllZones}
            onClearSelection={handleClearZoneSelection}
          />

          <BulkActionsLieux
            selectedIds={selectedZoneIds}
            type="zone"
            onUpdate={handleBulkUpdateZones}
            onDelete={handleBulkDeleteZones}
          />

          {/* Liste des zones avec groupement */}
          {groupedZones.map((group) => (
            <div key={group.key} className="space-y-4">
              {groupBy !== 'none' && (
                <h3 className="text-lg font-semibold text-gray-900 bg-gray-100 px-4 py-2 rounded-md">
                  {group.label}
                  <span className="ml-2 text-sm text-gray-500">
                    ({group.count} zone{group.count > 1 ? 's' : ''})
                  </span>
                </h3>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.items.map((zone: any) => (
                  <div key={zone.id} className="relative">
                    <ZoneCard
                      zone={zone}
                      onEdit={(zone) => handleEdit(zone, 'zone')}
                      onDelete={(zone) => handleDelete(zone.id, 'zone')}
                      onBulkPhotos={
                        bulkPhotoLieuEnabled && permissions.canCreate
                          ? (z) =>
                              openBulkPhotoModal({
                                type: 'zone',
                                id: z.id,
                                label: `${z.code} — ${z.nom}`,
                                subtitle: z.theme || undefined,
                              })
                          : undefined
                      }
                      isSelected={selectedZoneIds.includes(zone.id)}
                      onToggleSelection={() => handleToggleZoneSelection(zone.id)}
                      canEdit={permissions.canEdit}
                      canDelete={permissions.canDelete}
                      canBulk={permissions.canBulkEdit || permissions.canBulkDelete}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {groupedZones.length === 0 && (
            <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
              Aucune zone trouvée.
            </div>
          )}
        </div>
      )}

      {activeTab === 'boxes' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              Boxes Mobiles
              <span className="ml-2 text-sm text-gray-500">
                ({filteredBoxes.length} box{filteredBoxes.length > 1 ? 'es' : ''})
              </span>
            </h2>
            {permissions.canCreate && (
              <button
                onClick={() => { setEditingBox(null); setIsCreateBoxModalOpen(true); }}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                + Nouvelle Box
              </button>
            )}
          </div>

          {/* Recherche */}
          <MobileSearch
            value={filters.search}
            onChange={(value) => handleFilterChange('search', value)}
            placeholder="Rechercher une box..."
          />

          {/* Bouton toggle filtres */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="w-full md:w-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            {showFilters ? '🔽 Masquer les filtres' : '🔼 Afficher les filtres'}
          </button>

          {/* Filtres avancés */}
          {showFilters && (
            <LieuxFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
            />
          )}

          {/* Tri et groupement */}
          <SortGroupControls
            sortBy={sortBy}
            groupBy={groupBy}
            onSortChange={setSortBy}
            onGroupChange={setGroupBy}
            type="lieux"
            lieuType="boxes"
          />

          {/* Sélection et actions bulk */}
          <SelectionControls
            selectedIds={selectedBoxIds}
            totalCount={filteredBoxes.length}
            onSelectAll={handleSelectAllBoxes}
            onClearSelection={handleClearBoxSelection}
          />

          <BulkActionsLieux
            selectedIds={selectedBoxIds}
            type="box"
            onUpdate={handleBulkUpdateBoxes}
            onMove={handleBulkMoveBoxes}
            onDelete={handleBulkDeleteBoxes}
          />

          {/* Liste des boxes avec groupement */}
          {groupedBoxes.map((group) => (
            <div key={group.key} className="space-y-4">
              {groupBy !== 'none' && (
                <h3 className="text-lg font-semibold text-gray-900 bg-gray-100 px-4 py-2 rounded-md">
                  {group.label}
                  <span className="ml-2 text-sm text-gray-500">
                    ({group.count} box{group.count > 1 ? 'es' : ''})
                  </span>
                </h3>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {group.items.map((box: any) => (
                  <div key={box.id} className="relative">
                    <BoxCard
                      box={box}
                      onEdit={(box) => handleEdit(box, 'box')}
                      onDelete={(box) => handleDelete(box.id, 'box')}
                      onFillFromTheme={
                        permissions.canMove || permissions.canEdit
                          ? (b) => {
                              setBoxForBulkAdd(b);
                              setIsBulkAddToBoxOpen(true);
                            }
                          : undefined
                      }
                      onBulkPhotos={
                        bulkPhotoLieuEnabled && permissions.canCreate
                          ? (b) =>
                              openBulkPhotoModal({
                                type: 'box',
                                id: b.id,
                                label: `${b.code} — ${b.nom}`,
                                subtitle: b.theme || undefined,
                              })
                          : undefined
                      }
                      isSelected={selectedBoxIds.includes(box.id)}
                      onToggleSelection={() => handleToggleBoxSelection(box.id)}
                      canEdit={permissions.canEdit}
                      canDelete={permissions.canDelete}
                      canBulk={permissions.canBulkEdit || permissions.canBulkDelete}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {groupedBoxes.length === 0 && (
            <div className="bg-white shadow rounded-lg p-8 text-center text-gray-500">
              Aucune box trouvée.
            </div>
          )}
        </div>
      )}

      {/* Modal de création/édition */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingItem ? 'Modifier' : 'Créer'} {editingItem?.type === 'zone' ? 'Zone' : 'Box'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Code</label>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    defaultValue={editingItem?.code || ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <input
                    type="text"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    defaultValue={editingItem?.nom || ''}
                  />
                </div>
                {editingItem?.type === 'zone' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Thème</label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      defaultValue={editingItem?.theme || ''}
                      placeholder="Ex: Jeux extérieurs, Bureautique..."
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    // TODO: Implémenter la sauvegarde
                    setShowCreateForm(false);
                  }}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CreateSiteModal
        isOpen={isCreateSiteModalOpen}
        onClose={() => { setIsCreateSiteModalOpen(false); setEditingSite(null); }}
        onSubmit={handleCreateSite}
        editingSite={editingSite}
      />
      
      <CreateBatimentModal
        isOpen={isCreateBatimentModalOpen}
        onClose={() => { setIsCreateBatimentModalOpen(false); setEditingBatiment(null); }}
        onSubmit={handleCreateBatiment}
        sites={sites}
        editingBatiment={editingBatiment}
      />
      
      <CreatePieceModal
        isOpen={isCreatePieceModalOpen}
        onClose={() => { setIsCreatePieceModalOpen(false); setEditingPiece(null); }}
        onSubmit={handleCreatePiece}
        batiments={batiments}
        editingPiece={editingPiece}
      />
      
      <CreateZoneModal
        isOpen={isCreateZoneModalOpen}
        onClose={() => { setIsCreateZoneModalOpen(false); setEditingZone(null); }}
        onSubmit={handleCreateZone}
        pieces={pieces}
        editingZone={editingZone}
      />
      
      <CreateBoxModal
        isOpen={isCreateBoxModalOpen}
        onClose={() => { setIsCreateBoxModalOpen(false); setEditingBox(null); }}
        onSubmit={handleCreateBox}
        pieces={pieces}
        zones={zones}
        editingBox={editingBox}
      />

      {/* Modals Bulk Sites */}
      <BulkUpdateSitesModal
        isOpen={isBulkUpdateSitesOpen}
        onClose={() => setIsBulkUpdateSitesOpen(false)}
        selectedSites={sites.filter(site => selectedSiteIds.includes(site.id))}
        onSuccess={() => {
          setSelectedSiteIds([]);
          setIsBulkUpdateSitesOpen(false);
        }}
      />

      <BulkDeleteSitesModal
        isOpen={isBulkDeleteSitesOpen}
        onClose={() => setIsBulkDeleteSitesOpen(false)}
        selectedSites={sites.filter(site => selectedSiteIds.includes(site.id))}
        onSuccess={() => {
          setSelectedSiteIds([]);
          setIsBulkDeleteSitesOpen(false);
        }}
      />

      {/* Modals Bulk Batiments */}
      <BulkUpdateBatimentsModal
        isOpen={isBulkUpdateBatimentsOpen}
        onClose={() => setIsBulkUpdateBatimentsOpen(false)}
        selectedBatiments={batiments.filter(batiment => selectedBatimentIds.includes(batiment.id))}
        onSuccess={() => {
          setSelectedBatimentIds([]);
          setIsBulkUpdateBatimentsOpen(false);
        }}
      />

      <BulkDeleteBatimentsModal
        isOpen={isBulkDeleteBatimentsOpen}
        onClose={() => setIsBulkDeleteBatimentsOpen(false)}
        selectedBatiments={batiments.filter(batiment => selectedBatimentIds.includes(batiment.id))}
        onSuccess={() => {
          setSelectedBatimentIds([]);
          setIsBulkDeleteBatimentsOpen(false);
        }}
      />

      {/* Modals Bulk Pieces */}
      <BulkUpdatePiecesModal
        isOpen={isBulkUpdatePiecesOpen}
        onClose={() => setIsBulkUpdatePiecesOpen(false)}
        selectedPieces={pieces.filter(piece => selectedPieceIds.includes(piece.id))}
        onSuccess={() => {
          setSelectedPieceIds([]);
          setIsBulkUpdatePiecesOpen(false);
        }}
      />

      <BulkDeletePiecesModal
        isOpen={isBulkDeletePiecesOpen}
        onClose={() => setIsBulkDeletePiecesOpen(false)}
        selectedPieces={pieces.filter(piece => selectedPieceIds.includes(piece.id))}
        onSuccess={() => {
          setSelectedPieceIds([]);
          setIsBulkDeletePiecesOpen(false);
        }}
      />

      {/* Modals Bulk Zones */}
      <BulkUpdateZonesModal
        isOpen={isBulkUpdateZonesOpen}
        onClose={handleCloseBulkZonesModal}
        selectedIds={selectedZoneIds}
      />

      <BulkDeleteZonesModal
        isOpen={isBulkDeleteZonesOpen}
        onClose={handleCloseBulkZonesModal}
        selectedIds={selectedZoneIds}
      />

      {/* Modals Bulk Boxes */}
      <BulkUpdateBoxesModal
        isOpen={isBulkUpdateBoxesOpen}
        onClose={handleCloseBulkBoxesModal}
        selectedIds={selectedBoxIds}
      />

      <BulkMoveBoxesModal
        isOpen={isBulkMoveBoxesOpen}
        onClose={handleCloseBulkBoxesModal}
        selectedIds={selectedBoxIds}
      />

      <BulkDeleteBoxesModal
        isOpen={isBulkDeleteBoxesOpen}
        onClose={handleCloseBulkBoxesModal}
        selectedIds={selectedBoxIds}
      />

      <BulkAddToBoxModal
        isOpen={isBulkAddToBoxOpen}
        onClose={() => {
          setIsBulkAddToBoxOpen(false);
          setBoxForBulkAdd(null);
        }}
        box={boxForBulkAdd}
      />

      <BulkPhotoLieuModal
        isOpen={isBulkPhotoLieuOpen}
        onClose={() => {
          setIsBulkPhotoLieuOpen(false);
          setBulkPhotoTarget(null);
        }}
        target={bulkPhotoTarget}
      />
    </div>
  );
};

export default LieuxPage;